import { type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import { type Auth } from 'firebase/auth';
import { type Analytics } from 'firebase/analytics';
import { PUBLIC_FIRESTORE_DB, PUBLIC_STORAGE_BUCKET } from '$env/static/public';
import { type Firestore } from 'firebase/firestore';
import { singleton } from './utils';
import _ from 'lodash';

export type FirebaseService = {
	updateConsent: (consent: boolean) => Promise<void>;
	getApp: () => Promise<FirebaseApp>;
	getAuth: () => Promise<Auth>;
	getDb: () => Promise<Firestore>;
	getAnalytics: () => Promise<Analytics>;
	logEvent: (event: string, params?: Record<string, unknown>) => Promise<void>;
};

export type FirebaseServiceReduced = Omit<FirebaseService, 'getAuth'>;

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries
// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional

const baseFirebaseConfig = {
	apiKey: 'AIzaSyAeHIiKerzxNxoZlOMGNwYqgSeCR0xUnDA',
	authDomain: 'voice-align.firebaseapp.com',
	projectId: 'voice-align',
	storageBucket: PUBLIC_STORAGE_BUCKET,
	messagingSenderId: '101640875826'
} as FirebaseOptions;

const webpageFirebaseConfig = {
	...baseFirebaseConfig,
	measurementId: 'G-4F6R5MTYR6',
	appId: '1:101640875826:web:e4a2afcf9e38cbec23712b'
} as FirebaseOptions;

const pluginFirebaseConfig = {
	...baseFirebaseConfig,
	measurementId: 'G-JNCC9SWSP9',
	appId: '1:101640875826:web:3662cd9bb917e6d023712b'
} as FirebaseOptions;

export function createWebpageFirebaseService() {
	if (typeof window === 'undefined') {
		return createServerRenderingFirebaseService();
	}
	return createFirebaseService(webpageFirebaseConfig);
}

export function createPluginFirebaseService() {
	if (typeof window === 'undefined') {
		return createServerRenderingFirebaseService();
	}
	return createFirebaseService(pluginFirebaseConfig);
}

function createServerRenderingFirebaseService(): FirebaseService {
	return {
		async updateConsent() {},
		getApp() {
			throw Error('not available during server rendering');
		},
		getAuth() {
			throw Error('not available during server rendering');
		},
		getDb() {
			throw Error('not available during server rendering');
		},
		getAnalytics() {
			throw Error('not available during server rendering');
		},
		logEvent() {
			throw Error('not available during server rendering');
		}
	};
}

const createFirebaseService = singleton(
	'firebaseService',
	(config: FirebaseOptions): FirebaseService => {
		const getAppOnce = _.once(async () => {
			const { initializeApp } = await import('firebase/app');
			return initializeApp(config);
		});
		const getAuthOnce = _.once(async () => {
			const { getAuth } = await import('firebase/auth');
			return getAuth(await getAppOnce());
		});
		const getDbOnce = _.once(async () => {
			const { initializeFirestore, persistentLocalCache, persistentMultipleTabManager } =
				await import('firebase/firestore');
			return initializeFirestore(
				await getAppOnce(),
				{ localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) },
				PUBLIC_FIRESTORE_DB
			);
		});
		const getAnalyticsOnce = _.once(async () => {
			const { setConsent, getAnalytics } = await import('firebase/analytics');
			const app = await getAppOnce();
			setConsent({
				ad_personalization: 'denied',
				ad_storage: 'denied',
				ad_user_data: 'denied',
				analytics_storage: 'denied',
				functionality_storage: 'granted',
				personalization_storage: 'denied',
				security_storage: 'granted'
			});
			return getAnalytics(app);
		});

		return {
			async updateConsent(consent: boolean) {
				const { setConsent } = await import('firebase/analytics');
				setConsent({
					ad_personalization: 'denied',
					ad_storage: 'denied',
					ad_user_data: 'denied',
					analytics_storage: consent ? 'granted' : 'denied',
					functionality_storage: 'granted',
					personalization_storage: 'denied',
					security_storage: 'granted'
				});
			},
			getApp: getAppOnce,
			getAuth: getAuthOnce,
			getDb: getDbOnce,
			getAnalytics: getAnalyticsOnce,
			async logEvent(event: string, params?: Record<string, unknown>) {
				const { logEvent } = await import('firebase/analytics');
				logEvent(await getAnalyticsOnce(), event, params);
			}
		};
	}
);
